function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // Don't close mobile facets details elements - they should remain open
      if (!details.classList.contains('mobile-facets__details')) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      } else {
        // Keep mobile facets details open but remove menu-opening class
        details.classList.remove('menu-opening');
      }
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    const section = this.closest('.shopify-section');
    if (section) {
      this.dataset.section = section.id.replace('shopify-section-', '');
    } else {
      console.warn('No .shopify-section parent found.');
    }
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');

    if (this.classList.contains('slider-with-buttons')) {
      this.prevButton = this.querySelectorAll('button[name="previous"]');
      this.nextButton = this.querySelectorAll('button[name="next"]');
    } else {
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
    }


    this.paginationContainer = this.querySelector('.slideshow__control-wrapper.custom-pagination');
    this.paginationContainerReady = this.querySelector('.slideshow__control-wrapper.custom-pagination.created') || this.querySelector('.slider-buttons.quick-add-hidden');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.classList.contains('slider-with-buttons')) {
      this.prevButton.forEach(button => button.addEventListener('click', this.onButtonClick.bind(this)));
      this.nextButton.forEach(button => button.addEventListener('click', this.onButtonClick.bind(this)));
    } else {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );

    if (this.paginationContainerReady) {
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
      if (this.classList.contains('slider-style-columns')) {
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      }
    } else {
      if (this.slidesPerPage < 1) {
        this.slidesPerPage = 1;
      }

      const windowWidth = window.innerWidth;
      if (windowWidth > 989) {
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
      } else {
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      }
    }

    this.update();
    this.generatePagination();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  getVisibleSlides() {
    return Array.from(this.sliderItemsToShow).filter(slide => this.isSlideVisible(slide));
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1],
        },
      }));
    }

    if (this.enableSliderLooping) return;

    const isFirstSlideVisible = this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0;
    const isLastSlideVisible = this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]);

    if (this.classList.contains('slider-with-buttons')) {
      this.prevButton.forEach(button => {
        if (isFirstSlideVisible) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      });

      this.nextButton.forEach(button => {
        if (isLastSlideVisible) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      });
    } else {
      if (isFirstSlideVisible) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (isLastSlideVisible) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }

    // visible class
    this.sliderItemsToShow.forEach(slide => slide.classList.remove('slide-visible'));
    const visibleSlides = this.getVisibleSlides();
    visibleSlides.forEach(slide => slide.classList.add('slide-visible'));

    this.updatePagination();
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  generatePagination() {
    if (!this.paginationContainer) return;

    if (!this.paginationContainerReady) {
      this.paginationContainer.innerHTML = '';
    }

    for (let i = 0; i < this.totalPages; i++) {

      let paginationButton;
      if (!this.paginationContainerReady) {
        paginationButton = document.createElement('button');
        paginationButton.className = 'slider-counter__link slider-counter__link--dots link';
        paginationButton.setAttribute('aria-label', `Slide ${i + 1}`);
        paginationButton.setAttribute('aria-controls', this.slider.id);
        paginationButton.dataset.index = i + 1;

        const dotSpan = document.createElement('span');
        dotSpan.className = 'dot';
        paginationButton.appendChild(dotSpan);
      } else if (this.paginationContainerReady) {
        paginationButton = this.querySelector('button');
      }

      paginationButton.addEventListener('click', (event) => {
        const index = event.currentTarget.dataset.index;
        this.setSlidePosition((index - 1) * this.sliderItemOffset);
      });

      if (i === 0) {
        paginationButton.classList.add('slider-counter__link--active');
      }

      this.paginationContainer.appendChild(paginationButton);
    }
  }

  updatePagination() {
    if (!this.paginationContainer) return;

    const paginationButtons = this.paginationContainer.querySelectorAll('.slider-counter__link--dots');
    paginationButtons.forEach((button, index) => {
      if (index + 1 === this.currentPage) {
        button.classList.add('slider-counter__link--active');
      } else {
        button.classList.remove('slider-counter__link--active');
      }
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener('click', () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          {
            once: true
          }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);


class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.productId != undefined) {
      this.initializeRecommendations(this.dataset.productId);
    }
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);


class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



class ImageHotspot extends HTMLElement {
  constructor() {
    super();
    this.hotspotItems = this.querySelectorAll('.hotspot-item-icon');
    this.bindEvents = this.bindEvents.bind(this);
    this.closeAllHotspots = this.closeAllHotspots.bind(this);
  }

  connectedCallback() {
    if (this.hotspotItems.length) {
      this.bindEvents();

      document.addEventListener('click', this.closeAllHotspots);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.closeAllHotspots);
  }

  bindEvents() {
    this.hotspotItems.forEach((hotspotItem) => {
      const parentBubble = hotspotItem.closest('.hotspot-item');

      hotspotItem.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeAllHotspots();
        parentBubble.classList.add('active');
      });

      const closeBubble = parentBubble.querySelector('.bubble-close');
      if (closeBubble) {
        closeBubble.addEventListener('click', (event) => {
          event.stopPropagation();
          parentBubble.classList.remove('active');
        });
      }
    });
  }

  closeAllHotspots() {
    this.querySelectorAll('.hotspot-item').forEach((bubble) => {
      bubble.classList.remove('active');
    });
  }
}

customElements.define('image-hotspot', ImageHotspot);



const scrollToTopBtn = document.getElementById("scrollToTopBtn");
if (scrollToTopBtn) {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  scrollToTopBtn.addEventListener("click", scrollToTop);

  scrollToTopBtn.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      scrollToTop();
    }
  });
}

class MegaMenuFeaturedImages extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {

    this.initImages();

    this.megaMenuProductImage = document.querySelectorAll('header-menu a[data-product-image]');
    this.megaMenuProductImage.forEach(element =>
      element.addEventListener('mouseover', (event) => this.onChangeImage(event, 'mm-product-image', 'productImage', 'mm-collection-dropdown--second'))
    );

    this.megaMenuCollectionImage = document.querySelectorAll('header-menu summary[data-collection-image]');
    this.megaMenuCollectionImage.forEach(element =>
      element.addEventListener('click', (event) => this.onChangeImage(event, 'mm-collection-image', 'collectionImage', 'mega-menu__content'))
    );

  }

  initImages() {
    const collectionLinkParrent = this.closest('.header-collection-item');
    if (collectionLinkParrent) {
      const collectionLink = collectionLinkParrent.querySelector('summary[data-collection-image]');
      const productLink = collectionLinkParrent.querySelector('a[data-product-image]');

      if (collectionLink && collectionLink.dataset.collectionImage != 'false') {
        const linkDataImage = collectionLink.dataset.collectionImage;

        let collectionImage = document.createElement('img');
        collectionImage.className = 'mm-collection-image';
        this.appendChild(collectionImage);

        collectionImage.src = linkDataImage;
        collectionImage.srcset = linkDataImage;
      }

      if (productLink && productLink.dataset.productImage != 'false') {
        const linkDataImage = productLink.dataset.productImage;
        let productImage = document.createElement('img');
        productImage.className = 'mm-product-image';
        this.appendChild(productImage);

        productImage.src = linkDataImage;
        productImage.srcset = linkDataImage;
      }
    }
  }

  onChangeImage(event, imageClass, dataAttr, parentElement) {
    let linkDataImage = event.target.dataset[dataAttr];
    if (linkDataImage == 'false') {
      linkDataImage = false
    }

    if (linkDataImage && linkDataImage.length > 0) {
      const dropdown = event.target.closest(`.${parentElement}`);

      if (dropdown) {
        const existingImg = dropdown.querySelectorAll(`.js-megaMenuImgWrp .${imageClass}`);
        if (existingImg.length > 0) {
          existingImg.forEach((item) => {
            item.src = linkDataImage;
            item.srcset = linkDataImage;
          });
        } else {
          const newImg = document.createElement('img');
          newImg.className = imageClass;
          newImg.src = linkDataImage;
          newImg.srcset = linkDataImage;
          const container = dropdown.querySelector('.js-megaMenuImgWrp');
          if (container) {
            container.appendChild(newImg);
          }
        }
      }
    }
  }
}

customElements.define('variant-megamenu-img', MegaMenuFeaturedImages);


class ModalStoreSelector extends HTMLElement {
  constructor() {
    super();

    this.modalStorePopup = document.querySelector('.section-store-selector');
    this.modalStorePopupWrapper = document.querySelector('.select-store-popup__wrapper');
    this.closePopupButton = document.querySelector('.store-popup-heading__close');
    this.openPopupButtons = document.querySelectorAll('.store-selector-btn');
    this.lastTriggerButton = null;

    this.openPopupButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        this.lastTriggerButton = event.currentTarget;
        this.showModalStorePopup();
      });
    });

    this.closePopupButton.addEventListener('click', this.hideModalStorePopup.bind(this));

    this.closePopupButton.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.hideModalStorePopup();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.hideModalStorePopup();
      }
    });

    this.modalStorePopup.addEventListener('click', (event) => {
      if (!this.modalStorePopupWrapper.contains(event.target)) {
        this.hideModalStorePopup();
      }
    });
  }

  showModalStorePopup() {
    this.modalStorePopup.classList.add('show');
    setTimeout(() => {
      if (this.closePopupButton && this.modalStorePopup.classList.contains('show')) {
        this.closePopupButton.focus();
      }
    }, 150);
  }

  hideModalStorePopup() {
    this.modalStorePopup.classList.remove('show');

    if (this.lastTriggerButton) {
      setTimeout(() => this.lastTriggerButton.focus(), 100);
    }
  }
}

customElements.define('modal-store-selector', ModalStoreSelector);



class CardShare extends HTMLElement {
  constructor() {
    super();
    this.handleShare = this.handleShare.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.share-button');
    if (this.button) {
      this.button.addEventListener('click', this.handleShare);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleShare);
    }
  }

  handleShare() {
    const shareUrl = this.getAttribute('data-share-url');
    if (navigator.share) {
      navigator.share({
        url: shareUrl,
        title: document.title
      }).catch((error) => console.error('Share failed', error));
    } else {
      window.prompt("Copy url:", shareUrl);
    }
  }
}

customElements.define('card-share', CardShare);


class TrioShowcaseParallax extends HTMLElement {
  constructor() {
    super();
    this.imagesLeft = Array.from(this.querySelectorAll(".trio-showcase__images--left .trio-showcase__media-image"));
    this.imagesRight = Array.from(this.querySelectorAll(".trio-showcase__images--right .trio-showcase__media-image"));
    this.mouseX = 0;
    this.mouseY = 0;
    this.targetX = 0;
    this.targetY = 0;
    this.animationFrame = null;
    this.handleResize = this.handleResize.bind(this);
    this.initParallax();
  }
  initParallax() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }
  handleResize() {
    if (window.innerWidth >= 1100) {
      this.enableParallax();
    } else {
      this.disableParallax();
    }
  }
  enableParallax() {
    document.addEventListener("mousemove", this.updateMousePosition);
    this.startAnimation();
  }
  disableParallax() {
    document.removeEventListener("mousemove", this.updateMousePosition);
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.resetTransform();
  }
  updateMousePosition = (event) => {
    const { clientX, clientY } = event;
    const { innerWidth, innerHeight } = window;

    this.mouseX = (clientX / innerWidth - 0.5) * 2;
    this.mouseY = (clientY / innerHeight - 0.5) * 2;
  };
  resetTransform() {
    [...this.imagesLeft, ...this.imagesRight].forEach((img) => {
      img.style.transform = `translate(0px, 0px)`;
    });
  }
  startAnimation() {
    const animate = () => {
      this.targetX += (this.mouseX - this.targetX) * 0.08;
      this.targetY += (this.mouseY - this.targetY) * 0.08;

      const updateImageTransform = (images) => {
        const midIndex = Math.floor(images.length / 2);

        images.forEach((img, index) => {
          const distanceFromCenter = Math.abs(midIndex - index) / midIndex;
          const speedFactor = 0.3 + distanceFromCenter * 0.15;
          const offsetFactor = 50 + distanceFromCenter * 25;

          const moveX = this.targetX * offsetFactor * speedFactor;
          const moveY = this.targetY * offsetFactor * speedFactor;

          img.style.transform = `translate(${moveX}px, ${moveY}px)`;
        });
      };

      updateImageTransform(this.imagesLeft);
      updateImageTransform(this.imagesRight);

      this.animationFrame = requestAnimationFrame(animate);
    };
    animate();
  }
}
customElements.define("trio-showcase-parallax", TrioShowcaseParallax);



// product note
class AddProductNote extends HTMLElement {
  constructor() {
    super();
    this.toggleTextArea = this.toggleTextArea.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.heading = this.querySelector('.product-note-heading');
    this.textArea = this.querySelector('.customer-field-textarea');

    if (this.heading && this.textArea) {
      this.heading.setAttribute('role', 'button');
      this.heading.setAttribute('tabindex', '0');
      this.heading.addEventListener('click', this.toggleTextArea);
      this.heading.addEventListener('keydown', this.handleKeyDown);
    }
  }

  disconnectedCallback() {
    if (this.heading) {
      this.heading.removeEventListener('click', this.toggleTextArea);
      this.heading.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  toggleTextArea() {
    this.textArea.classList.toggle('active');
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleTextArea();
    }
  }
}

customElements.define('add-product-note', AddProductNote);


// follow cursor
const follower = document.querySelector('.follower');
if (follower && window.innerWidth >= 1025) {
  let targetX = 0, targetY = 0;
  let currentX = 0, currentY = 0;
  const ease = 0.1;
  const hiddenClasses = [
    'button', 'link', 'shopify-payment-button__button', 'header__icon', 'notification-close-popup', 'welcome-close-popup', 'account-icon', 'breadcrumbs__link', 'custom-link', 'quick-add__submit', 'button--secondary', 'quantity__input', 'quantity__button', 'pick-up__accordion--icon', 'share-button', 'quick-add__submit', 'product__accordion--icon', 'swiper-scrollbar', 'swiper-scrollbar-drag', 'field__button', 'field__input', 'icon-microphone'
  ];
  document.addEventListener('mousemove', (e) => {
    targetX = e.clientX - 12;
    targetY = e.clientY - 12;
  });
  function animateFollower() {
    currentX += (targetX - currentX) * ease;
    currentY += (targetY - currentY) * ease;
    follower.style.transform = `translate(${currentX}px, ${currentY}px)`;
    requestAnimationFrame(animateFollower);
  }
  function checkHover(element) {
    return hiddenClasses.some(cls => element.classList.contains(cls));
  }
  document.addEventListener('mouseover', (e) => {
    if (checkHover(e.target)) {
      follower.style.opacity = '0';
    }
  });
  document.addEventListener('mouseout', (e) => {
    if (checkHover(e.target)) {
      follower.style.opacity = '1';
    }
  });

  document.addEventListener('mouseenter', () => {
    follower.style.opacity = '1';
  });
  document.addEventListener('mouseleave', () => {
    follower.style.opacity = '0';
  });
  animateFollower();
}
// follow cursor end


// collection list animation
class FeaturedCollections extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.direction = 1;
    this.interval = null;
    this.resumeTimeout = null;
  }

  connectedCallback() {
    this.slider = this.querySelector(".slider-mobile-gutter");
    this.items = [...this.querySelectorAll(".collection-list__item")];

    if (!this.slider || this.items.length === 0) return;

    this.items.forEach((item) => {
      item.addEventListener("mouseenter", () => this.stopAnimation(true));
      item.addEventListener("mouseleave", () => this.resetAnimation());
      item.addEventListener("focus", () => this.stopAnimation(true));
      item.addEventListener("blur", () => this.resetAnimation());
      item.addEventListener("focusin", () => this.stopAnimation(true));
      item.addEventListener("focusout", () => this.resetAnimation());
    });

    this.checkVisibility();

    this.observer = new MutationObserver(() => this.checkVisibility());
    this.observer.observe(this.slider, { attributes: true, attributeFilter: ["class"] });
  }

  disconnectedCallback() {
    this.stopAnimation();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  startAnimation() {
    if (!this.interval) {
      this.index = 0;
      this.interval = setInterval(() => {
        this.items.forEach((item) => item.classList.remove("active"));
        this.items[this.index].classList.add("active");

        this.index += this.direction;

        if (this.index === this.items.length - 1 || this.index === 0) {
          this.direction *= -1;
        }
      }, 1400);
    }
  }

  stopAnimation(userAction = false) {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
      this.items.forEach((item) => item.classList.remove("active"));
    }

    if (userAction && this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = null;
    }
  }

  resetAnimation() {
    if (this.resumeTimeout) return;

    this.resumeTimeout = setTimeout(() => {
      this.startAnimation();
      this.resumeTimeout = null;
    }, 8000);
  }

  checkVisibility() {
    if (!this.slider.classList.contains("scroll-trigger--offscreen")) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  }
}

customElements.define("featured-collections", FeaturedCollections);

// collection list animation


// scrollbar
function setScrollbarWidth() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.setProperty('--scrollbar-width', scrollbarWidth > 0 ? `${scrollbarWidth}px` : '0px');
}
document.addEventListener('DOMContentLoaded', setScrollbarWidth);
window.addEventListener('resize', setScrollbarWidth);


// video alt
document.addEventListener('DOMContentLoaded', () => {
  const videos = document.querySelectorAll('video.video-for-alt');

  videos.forEach(video => {
    const img = video.querySelector('img');
    if (img && !img.hasAttribute('alt')) {
      const videoAlt = video.getAttribute('alt');
      if (videoAlt) {
        img.setAttribute('alt', videoAlt);
      }
    }
  });
});


class HighlightedWord extends HTMLElement {
  constructor() {
    super();
    this.words = [];
    this.currentIndex = 0;
    this.interval = null;
  }

  connectedCallback() {
    this.words = this.querySelectorAll('span');

    if (this.words.length > 0) {
      this.words[this.currentIndex].classList.add('active');
      this.interval = setInterval(() => this.changeWord(), 3000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  changeWord() {
    this.words[this.currentIndex].classList.remove('active');
    this.currentIndex = (this.currentIndex + 1) % this.words.length;
    this.words[this.currentIndex].classList.add('active');
  }
}

customElements.define('highlighted-word', HighlightedWord);


// email validation
document.addEventListener('DOMContentLoaded', addEmailInputsValidation);

function addEmailInputsValidation() {
  const emailInputs = document.querySelectorAll("input[type='email'].custom_validate");
  if (emailInputs.length === 0) return;

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  emailInputs.forEach(input => {
    const form = input.closest("form");
    const submitBtn = form?.querySelector("button[type='submit'], input[type='submit']");

    if (!submitBtn) return;

    let message = form.querySelector(".form__message_custom") || form.querySelector(".form__message");

    if (!message) {
      message = document.createElement("small");
      message.className = "newsletter-form__message form__message form__message_custom";

      const wrapper =
        input.closest('.newsletter-form__field-wrapper') ||
        input.closest('.form-wrapp');

      if (wrapper) {
        wrapper.appendChild(message);
      } else {
        input.insertAdjacentElement("afterend", message);
      }
    }

    const validateEmail = () => {
      const emailValue = input.value.trim();
      const isValid = emailPattern.test(emailValue);

      if (!isValid) {
        input.classList.add("invalid");
        submitBtn.setAttribute("disabled", "true");
        if (message) {
          message.textContent = window.forms?.emailError || "Please enter a valid email.";
        }

        if (emailValue === "") {
          input.classList.remove("invalid");
          submitBtn.removeAttribute("disabled");
          if (message) message.textContent = "";
        }
      } else {
        input.classList.remove("invalid");
        submitBtn.removeAttribute("disabled");
        if (message) message.textContent = "";
      }
    };

    const debouncedValidateEmail = debounce(validateEmail, 300);
    input.addEventListener("input", debouncedValidateEmail);
    input.addEventListener("change", debouncedValidateEmail);

    input.addEventListener("focus", () => {
      const emailValue = input.value.trim();
      const isValid = emailPattern.test(emailValue);

      if (!isValid) {
        input.classList.add("invalid");
        submitBtn.setAttribute("disabled", "true");
        if (message) {
          message.textContent = window.forms?.emailError || "Please enter a valid email.";
        }

        if (emailValue === "") {
          input.classList.remove("invalid");
          submitBtn.removeAttribute("disabled");
          if (message) message.textContent = "";
        }
      } else {
        input.classList.remove("invalid");
        submitBtn.removeAttribute("disabled");
        if (message) message.textContent = "";
      }
    });
  });
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}


document.addEventListener('DOMContentLoaded', () => {
  const drawer  = document.querySelector('#Details-menu-drawer-container');

  // Check if drawer exists before proceeding
  if (!drawer) return;

  const summary = drawer.querySelector('summary');
  const body    = document.body;

  drawer.addEventListener('toggle', () => {
    const isOpen = drawer.open;
    if (summary) {
      summary.setAttribute('aria-expanded', isOpen.toString());
    }
    if (isOpen) {
      body.classList.add('overflow-hidden-desktop');
    } else {
      body.classList.remove('overflow-hidden-desktop');
    }
  });

  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  drawer.addEventListener('keydown', (e) => {
    if (!drawer.open || e.key !== 'Tab') return;

    const nodes = Array.from(drawer.querySelectorAll(focusableSelectors))
      .filter(el => el.offsetParent !== null);
    if (nodes.length === 0) return;

    const first = nodes[0];
    const last  = nodes[nodes.length - 1];

    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      drawer.open = false;
      if (summary) summary.focus();
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      drawer.open = false;
      if (summary) summary.focus();
    }
  });

  drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.open) {
      drawer.open = false;
      if (summary) summary.focus();
    }
  });
});





console.log('Loka-2.0.0');